import React, { useState, useEffect, useMemo } from 'react';
import './Committee.css';
import profile from '../images/profile.png';
import { supabase } from '../supabaseClient';
import { resolveStorageUrl } from '../utils/storageUrl';
import ScientificCommitteeComite from './ScientificCommitteeComite';
// import FlagCarousel from './FlagCarousel';
// import PDFButtons from './PDFButtons';

function Committee({ edition: customEdition }) {
  const [aproposData, setAproposData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('scientific');
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    loadAproposData();
  }, [customEdition]);

  const loadAproposData = async () => {
    try {
      setLoading(true);
      
      let currentEditionYear = customEdition || '2025';
      let currentEditionId = null;

      if (!customEdition) {
        const { data: activeEdition } = await supabase
          .from('editions')
          .select('year, id')
          .eq('is_active', true)
          .single();
        
        if (activeEdition) {
          currentEditionYear = activeEdition.year;
          currentEditionId = activeEdition.id;
        }
      }

      if (!currentEditionId) {
        const { data: editionByYear } = await supabase
          .from('editions')
          .select('id')
          .eq('year', currentEditionYear)
          .single();
        
        if (editionByYear) {
          currentEditionId = editionByYear.id;
        }
      }

      if (!currentEditionId) {
        throw new Error(`Edition ${currentEditionYear} not found`);
      }

      const { data: mainData } = await supabase
        .from('apropos_data')
        .select('*')
        .eq('edition_id', currentEditionId)
        .single();

      const { data: committeeMembers } = await supabase
        .from('apropos_committee_members')
        .select('*')
        .eq('edition_id', currentEditionId)
        .order('display_order');

      const data = {
        committeeSection: {
          title: mainData?.committee_section_title || 'Comité d\'organisation du congrès',
        },
        committeeMembers: (committeeMembers || []).map(member => ({
          id: member.id,
          name: member.name,
          title: member.title,
          organization: member.organization,
          image: resolveStorageUrl(member.image_url, 'speaker-images', member.image_path) || profile,
          logo: resolveStorageUrl(member.company_logo_url, 'speaker-images', member.company_logo_path),
          is_honorary: member.is_honorary === 1 || member.is_honorary === '1' || member.is_honorary === true,
          is_organizing: member.is_organizing === 1 || member.is_organizing === '1' || member.is_organizing === true,
          is_president: member.is_president === 1 || member.is_president === '1' || member.is_president === true,
          is_scientific: member.is_scientific === 1 || member.is_scientific === '1' || member.is_scientific === true,
          is_expert: member.is_expert === 1 || member.is_expert === '1' || member.is_expert === true,
          order_president: member.order_president || 0,
          order_scientific: member.order_scientific || 0,
          order_honorary: member.order_honorary || 0,
          order_organizing: member.order_organizing || 0,
          order_expert: member.order_expert || 0,
          display_order: member.display_order || 0
        }))
      };

      setAproposData(data);
      setError(null);
    } catch (error) {
      console.error('Error loading committee data:', error);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (id) => {
    setExpanded(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const renderExpandableText = (text, id, maxLength = 40) => {
    if (!text) return null;
    if (text.length <= maxLength) return <span>{text}</span>;
    const isOpen = expanded[id];
    return (
      <>
        <span>
          {isOpen ? text : text.slice(0, maxLength) + '...'}
        </span>
        <button
          className="voir-plus-btn-comite"
          onClick={() => toggleExpand(id)}
          type="button"
          style={{color:"#000000ff",cursor:"pointer",marginLeft:4,fontWeight:500, background: 'none', border: 'none', textDecoration: 'underline'}}
        >
          {isOpen ? 'Voir moins' : 'Voir plus'}
        </button>
      </>
    );
  };

  const tabs = useMemo(() => {
    if (!aproposData) return [];
    const allTabs = [
      { id: 'scientific', label: 'Comité Scientifique', filter: m => m.is_scientific },
      { id: 'honorary', label: 'Comité d\'Honneur', filter: m => m.is_honorary} ,
      { id: 'expert', label: 'Experts Internationaux SSR', filter: m => m.is_expert },
      { id: 'president', label: 'Président du Congrès', filter: m => m.is_president },
      { id: 'organizing', label: 'Comité d\'Organisation', filter: m => m.is_organizing }
    ];
    return allTabs.filter(tab => 
      aproposData.committeeMembers.some(tab.filter)
    );
  }, [aproposData]);

  useEffect(() => {
    if (tabs.length > 0 && !tabs.find(t => t.id === activeTab)) {
      setActiveTab(tabs[0].id);
    }
  }, [tabs, activeTab]);

  const renderContent = () => {
    if (!aproposData) return null;

    const { committeeMembers } = aproposData;

    switch (activeTab) {
      case 'scientific':
        return (
          <ScientificCommitteeComite 
            members={committeeMembers
              .filter(m => m.is_scientific)
              .sort((a, b) => (a.order_scientific || 0) - (b.order_scientific || 0))} 
            renderExpandableText={renderExpandableText}
          />
        );
      case 'honorary':
        return (
          <div className="honorary-committee-list-comite honneur-list-comite">
            <h2>COMITE D'HONNEUR DU CONGRES</h2>
            <div className="committee-grid-comite uniform-grid-comite">
              {committeeMembers
                .filter(m => m.is_honorary)
                .sort((a, b) => (a.order_honorary || 0) - (b.order_honorary || 0))
                .map((member) => (
                  <div key={member.id} className="committee-member-comite">
                    <div className="member-photo-comite">
                      {member.logo && (
                        <div className="company-logo-comite" >
                          <img
                            src={member.logo}
                            alt="Logo"
                            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                            onError={(e) => { e.target.onerror = null; e.target.style.display = 'none'; }}
                          />
                        </div>
                      )}
                      <img
                        src={member.image || profile}
                        alt={member.name}
                        onError={(e) => { e.target.onerror = null; e.target.src = profile; }}
                      />
                    </div>
                    <div className="member-info-comite">
                      <h4 style={{ margin: 0, textAlign: 'center' }}>{member.name}</h4>
                      <p style={{ margin: 0, color: "#ce9b0eff" }}>{renderExpandableText(member.title, member.id + '-title')}</p>
                      <p>{renderExpandableText(member.organization, member.id + '-org')}</p>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        );
      case 'expert':
        return (
          <div className="expert-committee-list-comite expert-list-comite">
            <h2>EXPERTS INTERNATIONAUX GOUVERNANCE SECURITAIRE SSR</h2>
            <div className="committee-grid-comite uniform-grid-comite">
              {committeeMembers
                .filter(m => m.is_expert)
                .sort((a, b) => (a.order_expert || 0) - (b.order_expert || 0))
                .map((member) => (
                  <div key={member.id} className="committee-member-comite">
                    <div className="member-photo-comite">
                      {member.logo && (
                        <div className="company-logo-comite" >
                          <img
                            src={member.logo}
                            alt="Logo"
                            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                            onError={(e) => { e.target.onerror = null; e.target.style.display = 'none'; }}
                          />
                        </div>
                      )}
                      <img
                        src={member.image || profile}
                        alt={member.name}
                        onError={(e) => { e.target.onerror = null; e.target.src = profile; }}
                      />
                    </div>
                    <div className="member-info-comite">
                      <h4 style={{ margin: 0, textAlign: 'center' }}>{member.name}</h4>
                      <p>{renderExpandableText(member.title, member.id + '-title')}</p>
                      <p>{renderExpandableText(member.organization, member.id + '-org')}</p>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        );
      case 'president':
        return (
          <div className="honorary-committee-list-comite president-list-comite">
            <h2>PRÉSIDENT DU CONGRÈS</h2>
            <div className="committee-grid-comite">
              {committeeMembers
                .filter(m => m.is_president)
                .sort((a, b) => (a.order_president || 0) - (b.order_president || 0))
                .map((member) => (
                  <div key={member.id} className="committee-member-comite">
                    <div className="member-photo-comite">
                      {member.logo && (
                        <div className="company-logo-comite" >
                          <img
                            src={member.logo}
                            alt="Logo"
                            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                            onError={(e) => { e.target.onerror = null; e.target.style.display = 'none'; }}
                          />
                        </div>
                      )}
                      <img
                        src={member.image || profile}
                        alt={member.name}
                        onError={(e) => { e.target.onerror = null; e.target.src = profile; }}
                      />
                    </div>
                    <div className="member-info-comite">
                      <h4 style={{ margin: 0, textAlign: 'center' }}>{member.name}</h4>
                      <p>{renderExpandableText(member.title, member.id + '-title')}</p>
                      <p>{renderExpandableText(member.organization, member.id + '-org')}</p>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        );
      case 'organizing':
        return (
          <div className="organizing-committee-list-comite">
            <h2>{aproposData.committeeSection?.title || "Comité d'organisation du congrès"}</h2>
            <div className="committee-grid-comite organizing-grid-comite">
              {committeeMembers
                .filter(m => m.is_organizing)
                .sort((a, b) => (a.order_organizing || 0) - (b.order_organizing || 0))
                .map((member) => (
                  <div key={member.id} className="committee-member-comite">
                    <div className="member-photo-comite">
                      {member.logo && (
                        <div className="company-logo-comite" >
                          <img
                            src={member.logo}
                            alt="Logo"
                            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                            onError={(e) => { e.target.onerror = null; e.target.style.display = 'none'; }}
                          />
                        </div>
                      )}
                      <img
                        src={member.image || profile}
                        alt={member.name}
                        onError={(e) => { e.target.onerror = null; e.target.src = profile; }}
                      />
                    </div>
                    <div className="member-info-comite">
                      <h4 style={{ margin: 0, textAlign: 'center' }}>{member.name}</h4>
                      <p>{renderExpandableText(member.title, member.id + '-title')}</p>
                      <p>{renderExpandableText(member.organization, member.id + '-org')}</p>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="committee-page-comite">
        <div className="loading-spinner-comite"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="committee-page-comite">
        <div className="committee-container-comite">
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="committee-page-comite">
      <div className="committee-container-comite">
        <div className="committee-header-comite">
          <h1>Comités CIGSIDA</h1>
        </div>

        <div className="committee-tabs-comite">
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={`tab-btn-comite ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="committee-content-comite">
          <div className="congress-committee-comite">
            {renderContent()}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Committee;
